import React from "react";
import { AlertTriangle, CreditCard, Calendar, CheckCircle2, Loader2 } from "lucide-react";
import { WHOLESALE_PRICE_SUMMARY } from "@/constants/aiTiersCatalog";

const formatDate = (isoDate) => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
};

const ActionButton = ({ label, onClick, isSubmitting, variant = "primary", testId }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const styles =
    variant === "secondary"
      ? `${base} bg-[#00D2FF]/10 text-partner-accent border border-[#00D2FF]/30 hover:bg-[#00D2FF]/20 hover:border-[#00D2FF]/50`
      : `${base} bg-[#00D2FF] text-[#080A0F] border border-[#00D2FF] shadow-[0_0_20px_rgba(0,210,255,0.2)] hover:bg-[#00E5FF] hover:border-[#00E5FF] hover:shadow-[0_0_24px_rgba(0,210,255,0.35)]`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isSubmitting}
      className={styles}
      data-testid={testId}
    >
      {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
      {label}
    </button>
  );
};

const PartnerBillingSummary = ({
  billing,
  isSubmitting = false,
  onSetup,
  onPortal,
  actionError = "",
}) => {
  if (billing.status === "inactive") {
    return (
      <div
        className="rounded-2xl border border-partner bg-[#11151F] p-8 md:p-12 text-center"
        data-testid="partner-billing-empty"
      >
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00D2FF]/10 border border-[#00D2FF]/20">
          <CreditCard className="h-8 w-8 text-partner-accent" />
        </div>
        <h2 className="text-xl font-bold text-partner mb-2">No wholesale billing on file</h2>
        <p className="text-sm text-partner-muted font-body max-w-md mx-auto mb-6">
          Add a payment method to activate wholesale deployments. Retail billing to your clients
          stays outside the platform.
        </p>
        <ActionButton
          label="Add payment method"
          onClick={onSetup}
          isSubmitting={isSubmitting}
          testId="partner-billing-setup-button"
        />
        {actionError && (
          <p className="mt-4 text-sm text-red-400 font-body" data-testid="partner-billing-action-error">
            {actionError}
          </p>
        )}
      </div>
    );
  }

  const lineItems = billing.line_items || [];
  const total = billing.estimated_monthly_total ?? lineItems.reduce((sum, item) => sum + item.total, 0);
  const paymentLast4 = billing.payment_method?.last4;
  const paymentBrand = billing.payment_method?.brand;
  const isPastDue = billing.status === "past_due";

  return (
    <div className="space-y-6" data-testid="partner-billing-active">
      {isPastDue && (
        <div
          className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3"
          data-testid="partner-billing-past-due-alert"
        >
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-200">Payment issue</p>
            <p className="text-sm text-amber-100/80 font-body mt-1">
              Your last wholesale charge failed. Update your payment method to keep deployments active.
            </p>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-partner bg-[#11151F] p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className={`w-5 h-5 ${isPastDue ? "text-amber-400" : "text-emerald-400"}`} />
              <span
                className={`text-xs font-mono tracking-wider uppercase ${
                  isPastDue ? "text-amber-400" : "text-emerald-400"
                }`}
              >
                {isPastDue ? "Past due" : "Ready"}
              </span>
            </div>
            <h2 className="text-xl font-bold text-partner">{billing.plan_label}</h2>
            {total === 0 && !isPastDue && (
              <p className="text-sm text-partner-muted font-body mt-2 max-w-lg">
                Payment method on file. Deploy clients to start wholesale billing. Charges apply
                automatically.
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-partner">${total}</p>
            <p className="text-xs text-partner-muted font-body">estimated this cycle</p>
          </div>
        </div>

        {lineItems.some((item) => item.qty > 0) && (
          <ul className="space-y-3 mb-6">
            {lineItems
              .filter((item) => item.qty > 0)
              .map((item) => (
                <li
                  key={item.label}
                  className="flex items-center justify-between text-sm font-body border-b border-partner pb-3 last:border-0 last:pb-0"
                >
                  <span className="text-partner-muted">{item.label}</span>
                  <span className="text-partner font-mono">${item.total}</span>
                </li>
              ))}
          </ul>
        )}

        <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-partner">
          {billing.current_period_end && (
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-partner-accent" />
              <div>
                <p className="text-xs text-partner-muted font-body">Next invoice</p>
                <p className="text-sm text-partner font-semibold">
                  {formatDate(billing.current_period_end)}
                </p>
              </div>
            </div>
          )}
          {paymentLast4 && (
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-partner-accent" />
              <div>
                <p className="text-xs text-partner-muted font-body">Payment method</p>
                <p className="text-sm text-partner font-semibold capitalize">
                  {paymentBrand ? `${paymentBrand} ` : ""}•••• {paymentLast4}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <ActionButton
          label={isPastDue ? "Update payment method" : "Manage billing"}
          onClick={onPortal}
          isSubmitting={isSubmitting}
          testId="partner-billing-portal-button"
        />
        {!isPastDue && (
          <ActionButton
            label="Update card"
            onClick={onPortal}
            isSubmitting={isSubmitting}
            variant="secondary"
            testId="partner-billing-update-card-button"
          />
        )}
      </div>

      {actionError && (
        <p className="text-sm text-red-400 font-body" data-testid="partner-billing-action-error">
          {actionError}
        </p>
      )}

      <p className="text-xs text-partner-muted font-body leading-relaxed">
        You bill your clients at retail rates on your own terms. This console tracks wholesale
        infrastructure costs only ({WHOLESALE_PRICE_SUMMARY} per active client tier).
      </p>
    </div>
  );
};

export default PartnerBillingSummary;
